import { IconCheck, IconDomains, IconEdit, IconPlus } from '@myplayer/ui';
import { useState, type ReactElement } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
  Input,
  ModalDialog,
  Switch,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildDefaultDomainContextMenuItems } from './context-menu-items.js';
import {
  DnsRecordsPanel,
  DomainFailureBanner,
  formatDomainStatusLabel,
  formatDomainStatusVariant,
  formatTlsStatusLabel,
  getDomainFailureReason,
  getDomainStatusHelpText,
  type CustomDomainItem,
} from './domains-dns-records.js';

export * from './domains-dns-records.js';

const RESERVED_PLATFORM_HOSTS: ReadonlySet<string> = new Set([
  'myplayr.cloud',
  'myplayr.co.uk',
  'admin.myplayr.cloud',
  'api.myplayr.cloud',
  'status.myplayr.cloud',
  'localhost',
  '127.0.0.1',
]);

export function isPlatformOrReservedDomain(
  rawHostname: string,
  platformHostname = 'myplayr.cloud',
): boolean {
  const cleaned = rawHostname.trim().toLowerCase().replace(/\.+$/, '');
  if (!cleaned) return false;
  const norm = platformHostname.trim().toLowerCase().replace(/\.+$/, '');
  const suffixes = ['.myplayr.cloud', '.myplayr.co.uk', '.run.app', '.web.app', '.firebaseapp.com'];
  return (
    RESERVED_PLATFORM_HOSTS.has(cleaned) ||
    cleaned === norm ||
    cleaned.endsWith(`.${norm}`) ||
    suffixes.some((s) => cleaned.endsWith(s))
  );
}

export function filterOutPlatformDomains(
  items: readonly CustomDomainItem[],
  platformHostname = 'myplayr.cloud',
): CustomDomainItem[] {
  return items.filter(
    (item) =>
      item.status !== 'DISABLED' && !isPlatformOrReservedDomain(item.hostname, platformHostname),
  );
}

export function mapSnapshotToDomains(
  docs: readonly { readonly id: string; data(): Partial<CustomDomainItem> }[],
  orgId: string,
  workspaceId: string,
  platformHostname = 'myplayr.cloud',
): CustomDomainItem[] {
  return filterOutPlatformDomains(
    docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        hostname: data.hostname ?? docSnap.id,
        orgId: data.orgId ?? orgId,
        workspaceId: data.workspaceId ?? workspaceId,
        status: data.status ?? 'PENDING_TXT',
        verificationToken: data.verificationToken ?? '',
        ...(data.txtHost !== undefined ? { txtHost: data.txtHost } : {}),
        tlsStatus: data.tlsStatus ?? 'PENDING',
        isPrimary: data.isPrimary ?? false,
        verifiedAt: data.verifiedAt ?? null,
        ...(data.plainStatus !== undefined ? { plainStatus: data.plainStatus } : {}),
        ...(data.cnameTarget !== undefined ? { cnameTarget: data.cnameTarget } : {}),
        ...(data.acmeChallengeRecord !== undefined
          ? { acmeChallengeRecord: data.acmeChallengeRecord }
          : {}),
        ...(data.failureReason !== undefined ? { failureReason: data.failureReason } : {}),
        createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
        updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : '',
        updatedBy: data.updatedBy ?? '',
      };
    }),
    platformHostname,
  );
}

export function validateCustomHostnameInput(
  rawHostname: string,
  platformHostname = 'myplayr.cloud',
):
  | { readonly ok: true; readonly hostname: string }
  | { readonly ok: false; readonly error: string } {
  const cleaned = rawHostname.trim().toLowerCase().replace(/\.+$/, '');
  const validShape =
    cleaned.length > 0 &&
    !cleaned.includes('://') &&
    !cleaned.includes('/') &&
    !cleaned.includes(' ') &&
    /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(
      cleaned,
    );
  if (!validShape) {
    return {
      ok: false,
      error:
        'Please enter a valid domain name without https:// or slashes (for example, id.yourbrand.com).',
    };
  }
  if (isPlatformOrReservedDomain(cleaned, platformHostname)) {
    return {
      ok: false,
      error: `Cannot add "${cleaned}" as a custom domain because it is reserved for or already served by the platform.`,
    };
  }
  return { ok: true, hostname: cleaned };
}

export function AddCustomDomainModal({
  open,
  workspaceId,
  hostnameInput,
  setHostnameInput,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly orgId?: string;
  readonly workspaceId: string;
  readonly effectiveHostname?: string;
  readonly hostnameInput: string;
  readonly setHostnameInput: (val: string) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (hostname: string, isPrimary: boolean) => void;
}): ReactElement {
  const [draftPrimary, setDraftPrimary] = useState(false);

  return (
    <ModalDialog
      open={open}
      title="Add Custom Domain"
      subtitle={`Connect a brand domain to ${workspaceId}`}
      icon={<IconDomains />}
      presentation="center"
      maxWidthClassName="max-w-2xl"
      submitting={submitting}
      onClose={onClose}
      data-testid="add-custom-domain-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-custom-domain-form"
            variant="primary"
            size="sm"
            disabled={submitting}
            className="gap-1.5"
          >
            <IconPlus />
            <span>Add Domain</span>
          </Button>
        </>
      }
    >
      <form
        id="add-custom-domain-form"
        aria-label="Add custom domain"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(hostnameInput, draftPrimary);
        }}
        className="space-y-4"
      >
        <div>
          <label
            htmlFor="custom-domain-hostname"
            className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
          >
            Custom Hostname
          </label>
          <Input
            id="custom-domain-hostname"
            type="text"
            value={hostnameInput}
            onChange={(e) => setHostnameInput(e.target.value)}
            placeholder="id.yourbrand.com"
            disabled={submitting}
          />
          <p className="mt-1 text-[11px] text-fg-secondary">
            Enter the subdomain you want printed QR codes to use (for example, id.yourbrand.com).
            After saving, the server will return the exact DNS TXT verification record and CNAME
            target to add at your DNS provider.
          </p>
        </div>

        <div
          className="border-t border-subtle pt-3.5 text-[12px]"
          data-testid="add-domain-advanced-section"
        >
          <Switch
            id="add-domain-primary-switch"
            checked={draftPrimary}
            onChange={(e) => setDraftPrimary(e.target.checked)}
            disabled={submitting}
            label="Use as primary domain for new QR codes"
          />
        </div>
        {errorMessage && (
          <p
            role="alert"
            data-testid="domain-form-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {errorMessage}
          </p>
        )}
      </form>
    </ModalDialog>
  );
}

export function EditCustomDomainModal({
  domain,
  submitting,
  errorMessage,
  onClose,
  onSave,
}: {
  readonly domain: CustomDomainItem | null;
  readonly effectiveHostname?: string;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSave: (domain: CustomDomainItem, isPrimary: boolean) => void;
}): ReactElement {
  const [draftPrimary, setDraftPrimary] = useState<boolean | null>(null);

  const effectivePrimary = draftPrimary ?? domain?.isPrimary ?? false;
  const failureReason = domain ? getDomainFailureReason(domain) : null;

  const closeModal = () => {
    setDraftPrimary(null);
    onClose();
  };

  return (
    <ModalDialog
      open={domain !== null}
      title={domain ? `Edit ${domain.hostname}` : 'Edit Domain'}
      subtitle="Update routing role, copy DNS records, or save and verify domain"
      icon={<IconEdit />}
      presentation="center"
      maxWidthClassName="max-w-2xl"
      submitting={submitting}
      onClose={closeModal}
      data-testid="edit-domain-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={closeModal}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={submitting}
            data-testid="save-domain-modal-btn"
            onClick={() => {
              if (domain) {
                onSave(domain, effectivePrimary);
                setDraftPrimary(null);
              }
            }}
          >
            <IconCheck />
            <span>Save</span>
          </Button>
        </>
      }
    >
      {domain && (
        <div className="space-y-4 text-[12px]">
          <div className="divide-y divide-subtle border-y border-subtle">
            <CardRow label="Domain">
              <span className="font-mono font-semibold text-fg">{domain.hostname}</span>
            </CardRow>
            <CardRow label="Status">
              <Badge variant={formatDomainStatusVariant(domain.status, domain.tlsStatus)}>
                {formatDomainStatusLabel(domain.status, domain.tlsStatus)}
              </Badge>
            </CardRow>
            <CardRow label="HTTPS certificate">
              <Badge variant={domain.tlsStatus === 'ACTIVE' ? 'valid' : 'outline'}>
                {formatTlsStatusLabel(domain.tlsStatus)}
              </Badge>
            </CardRow>
          </div>

          {failureReason !== null && (
            <DomainFailureBanner hostname={domain.hostname} reason={failureReason} />
          )}

          <p className="text-[12px] leading-relaxed text-fg-secondary">
            {getDomainStatusHelpText(domain)}
          </p>

          <div data-testid="domain-advanced-section">
            <DnsRecordsPanel
              hostname={domain.hostname}
              {...(domain.txtHost !== undefined ? { txtHost: domain.txtHost } : {})}
              verificationToken={domain.verificationToken}
              {...(domain.cnameTarget !== undefined ? { cnameTarget: domain.cnameTarget } : {})}
              {...(domain.acmeChallengeRecord !== undefined
                ? { acmeChallengeRecord: domain.acmeChallengeRecord }
                : {})}
              testIdPrefix="edit-domain-dns-records"
            />
          </div>

          <Switch
            id="edit-domain-primary-switch"
            checked={effectivePrimary}
            onChange={(e) => setDraftPrimary(e.target.checked)}
            disabled={submitting}
            label="Use as primary domain for new QR codes"
          />

          {errorMessage !== null && (
            <p
              role="alert"
              className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
            >
              {errorMessage}
            </p>
          )}
        </div>
      )}
    </ModalDialog>
  );
}

export function DefaultDomainCard({
  effectiveOrigin,
}: {
  readonly effectiveOrigin: string;
}): ReactElement {
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  return (
    <Card
      className="xl:col-span-4"
      data-testid="default-domain-tile"
      onContextMenu={bindContextMenu(() => buildDefaultDomainContextMenuItems({ effectiveOrigin }))}
    >
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <CardIcon>
            <IconDomains />
          </CardIcon>
          <CardTitle>Platform Default Domain</CardTitle>
        </div>
        <Badge variant="success">Active</Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <CardDescription>Always-on web address for your product QR codes</CardDescription>
        <div className="divide-y divide-subtle border-t border-subtle">
          <CardRow label="Default web address">
            <span className="truncate font-mono text-[13px] font-semibold text-fg">
              {effectiveOrigin}
            </span>
          </CardRow>
          <CardRow label="HTTPS security">
            <span className="text-[12px] text-fg-secondary">Active · Automatic certificate</span>
          </CardRow>
        </div>
        {contextMenuElement}
      </CardContent>
    </Card>
  );
}
